import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PartyEvent } from '../../model/PartyEvent'
import { WrappedEventTask } from '../../model/WrappedEventTask'
import { insertItemAtIndex, removeItemAtIndex } from '../../utils/arrayUtils'
import { getEventTasksAsync, deleteEventTaskAsync } from './currentPartyEventAPI'
import { RootState } from '../../app/store';
import { storeEventTaskAsync } from '../currentEventTask/currentEventTaskSlice'
import { dateToDisplayableDate, dateToDisplayableDateTime, dateToInputDate, dispayDateAndTimeToTransmissionDate, transmissionDateToDisplayableDate, transmissionDateToDisplayableTime, transmissionDateToInputDate} from '../../utils/dateUtils'
import { EventTask } from '../../model/EventTask'

export const findWrappedEventIndexById = (
  items: WrappedEventTask[],
  id: number
) => {
  return items.findIndex((item: WrappedEventTask) => item.eventTask.id === id)
}

export interface CurrentPartyEventState {
    partyEvent: PartyEvent | null
    wrappedEventTasks: WrappedEventTask[]
    filteredEventTasks: WrappedEventTask[]
    status: 'idle' | 'loading' | 'failed'
    filter: string
    displayFilter: boolean
    refreshRequired: boolean
    logoutRequested: boolean
}

const initialState: CurrentPartyEventState = {
    partyEvent: null,
    wrappedEventTasks: [],
    filteredEventTasks: [],
    status: 'idle',
    filter: '',
    displayFilter: false,
    refreshRequired: true,
    logoutRequested: false
}

  export interface GetEventTasksRequest {
      partyEventId: number
      token: string
  }

export interface DeleteEventTaskRequest {
  eventTaskId: number
  token: string
  userEmail: string
}

export interface DeleteEventTaskResponse {
  eventTaskId: number
  success: boolean
}

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const loadEventTasksAsync = createAsyncThunk(
    'currentPartyEvent/fetchEventTasks',
    async (request: GetEventTasksRequest) => {
      const response = await getEventTasksAsync(request.partyEventId, request.token)
      // The value we return becomes the `fulfilled` action payload
      return response;
    }
  );

export const removeEventTaskAsync = createAsyncThunk(
  'currentPartyEvent/removeEventTask',
  async (request: DeleteEventTaskRequest) => {
    const apiResponse = await deleteEventTaskAsync(request.eventTaskId, request.userEmail, request.token)
    const response: DeleteEventTaskResponse = {
      eventTaskId: request.eventTaskId,
      success: apiResponse
    }
    return response
  }
)

export const currentPartyEventSlice = createSlice({
    name: 'currentPartyEvent',
    initialState: initialState,
    reducers: {
        associatePartyEvent: (state, action: PayloadAction<PartyEvent>) => {
          state.partyEvent = action.payload
          state.status = 'idle'
          state.refreshRequired = true
        },
        setEventTitle: (state, action: PayloadAction<string>) => {
          if (state.partyEvent != null) {
            state.partyEvent.title = action.payload
          }
        },
        setEventLocation: (state, action: PayloadAction<string>) => {
          if (state.partyEvent != null) {
            state.partyEvent.location = action.payload
          }
        },

        setStartDate : (state, action: PayloadAction<string>) => {
          if (state.partyEvent != null) {
            state.partyEvent.start = action.payload
          if (state.partyEvent.end && state.partyEvent.end<state.partyEvent.start) {
              state.partyEvent.end = state.partyEvent.start
          }
          }
        },

        setEndDate : (state, action: PayloadAction<string>) => {
          if (state.partyEvent != null) {
              state.partyEvent.end = action.payload
              
            }
        },

        setDisplayTasksFilter : (state, action: PayloadAction<boolean>) => {
          state.displayFilter = action.payload
        },
        setEventTasksFilter : (state, action : PayloadAction<string>) => {
          state.filter = action.payload
          if (state.filter.trim().length==0) {
            state.filteredEventTasks = state.wrappedEventTasks
          }
          else {
            state.filteredEventTasks = []
            let capitalFilter : string = state.filter.toUpperCase()
            state.wrappedEventTasks.map((wrappedEventTask) => {
              let person : string | null = wrappedEventTask.eventTask.allocatedUserAlias ? wrappedEventTask.eventTask.allocatedUserAlias : wrappedEventTask.eventTask.allocatedUserEmail
                person = person ? person.toUpperCase() : ''

              if ( wrappedEventTask.eventTask.title.toUpperCase().indexOf(capitalFilter)>-1
                || person.indexOf(capitalFilter)>-1
                ) { 
                state.filteredEventTasks.push(wrappedEventTask)
              }
            }
        ) }
        },
        setLogoutRequested : (state, action: PayloadAction<boolean>) => {
          state.logoutRequested = action.payload
        }

    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
      builder
        .addCase(loadEventTasksAsync.pending, (state) => {
          state.status = 'loading';
        })
        .addCase(loadEventTasksAsync.fulfilled, (state, action) => {
          state.refreshRequired = false
          state.status = 'idle';
          state.wrappedEventTasks = action.payload;
          state.filter = '';
          state.filteredEventTasks = state.wrappedEventTasks
        })
        .addCase(loadEventTasksAsync.rejected, (state) => {
          state.status = 'failed';
        }).addCase(removeEventTaskAsync.pending, (state) => {
          state.status = 'loading';
        })
        .addCase(removeEventTaskAsync.fulfilled, (state, action) => {
          state.status = 'idle';
          if (action.payload.success) {
            const index = findWrappedEventIndexById(state.wrappedEventTasks, action.payload.eventTaskId)
            state.wrappedEventTasks = removeItemAtIndex(state.wrappedEventTasks, index)
            state.refreshRequired = true
          }
        })
        .addCase(removeEventTaskAsync.rejected, (state) => {
          state.status = 'failed';
        }).addCase(storeEventTaskAsync.pending, (state) => {
          state.status = 'loading';
        })
        .addCase(storeEventTaskAsync.fulfilled, (state, action) => {
          state.status = 'idle';
          if (action.payload != null) {
            const index = findWrappedEventIndexById(state.wrappedEventTasks, action.payload.eventTask.id)
            if (index < -1) {
              state.wrappedEventTasks.push(action.payload)
            } else {
              state.wrappedEventTasks = removeItemAtIndex(state.wrappedEventTasks, index)
              insertItemAtIndex(state.wrappedEventTasks, action.payload, index)
            }
            
              state.wrappedEventTasks.push(action.payload)
              state.refreshRequired = true
          }
        })
        .addCase(storeEventTaskAsync.rejected, (state) => {
          state.status = 'failed';
        });
    },
})

export const { associatePartyEvent, setEventTitle, setEventLocation, setDisplayTasksFilter, setEventTasksFilter, setStartDate, setEndDate, setLogoutRequested } = currentPartyEventSlice.actions
export const selectCurrentPartyEvent = (state: RootState) => state.currentPartyEvent.partyEvent
export const selectCurrentPartyWrappedEventTasks = (state: RootState) => state.currentPartyEvent.wrappedEventTasks
export const selectCurrentPartyEventTitle = (state: RootState) => state.currentPartyEvent.partyEvent?.title
export const selectCurrentPartyEventLocation = (state: RootState) => state.currentPartyEvent.partyEvent?.location
export const selectCurrentPartyEventDate = (state: RootState) => state.currentPartyEvent.partyEvent?.start
export const selectDisplayTaskFilter = (state: RootState) => state.currentPartyEvent.displayFilter
export const selectEventTasksFilter = (state: RootState) => state.currentPartyEvent.filter
export const selectCurrentPartyFilteredEventTasks = (state: RootState) => state.currentPartyEvent.filteredEventTasks
export const selectCurrentPartyRefreshRequired = (state: RootState) => state.currentPartyEvent.refreshRequired
export const selectCurrentPartyLogoutRequested = (state: RootState) => state.currentPartyEvent.logoutRequested

export default currentPartyEventSlice.reducer